import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Plus, XIcon } from 'lucide-react-native';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Keyboard, ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { EducationEntry } from '@/components/educationEntry';
import { Header } from '@/components/header';
import { Alert, AlertText } from '@/components/ui/alert';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useGoBack } from '@/hooks/useGoBack';
import { useToaster } from '@/hooks/useToaster';
import { getUserV2QueryOptions } from '@/services/getUserV2';
import { userIdAtom } from '@/services/globalState';
import { updateUser } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';
import { sortByYear, validateYear, validateYearRange } from '@/utils/profileHelpers';

interface Education {
  degree: string;
  institution: string;
  studyField: string;
  startYear?: string;
  endYear?: string;
  isPresent?: boolean;
}

const educationEntrySchema = z
  .object({
    degree: z.string().optional(),
    institution: z.string().min(1, 'Institution is required'),
    studyField: z.string().optional(),
    startYear: z.string().refine(validateYear, {
      message: 'Enter a valid 4-digit year',
    }),
    endYear: z.string().refine(validateYear, {
      message: 'Enter a valid 4-digit year',
    }),
    isPresent: z.boolean().optional(),
  })
  .refine(
    (data) => {
      return validateYearRange(data.startYear, data.endYear, data.isPresent || false);
    },
    {
      message: 'End year must be after start',
      path: ['endYear'],
    }
  );

const educationSchema = z.object({
  education: z.array(educationEntrySchema),
});

function EditEducationScreen() {
  const userId = useAtomValue(userIdAtom);

  const goBack = useGoBack(ROUTES.ACCOUNT_PROFILE);

  const queryClient = useQueryClient();

  const toaster = useToaster();

  const { data: userData, isLoading } = useQuery(getUserV2QueryOptions(userId));

  const { mutateAsync: updateUserMutation } = useMutation({ mutationFn: updateUser });

  const form = useForm<{ education: Education[] }>({
    defaultValues: {
      education: [],
    },
    resolver: zodResolver(educationSchema),
    mode: 'onSubmit',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'education',
  });

  const [expandedItem, setExpandedItem] = useState<string[]>([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const [deleting, setDeleting] = useState(false);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const keyboardAwareScrollRef = useRef<React.ComponentRef<typeof KeyboardAwareScrollView>>(null);

  const educationErrorCount = Array.isArray(form.formState.errors.education)
    ? form.formState.errors.education.filter((error) => error).length
    : 0;

  const onSubmit = async () => {
    try {
      setHasSubmitted(true);

      Keyboard.dismiss();

      const values = form.getValues();

      if (!values || !values.education) {
        throw new Error('Form values missing education data');
      }

      const filteredEducation = values.education.filter(
        (entry) =>
          entry.degree?.trim() ||
          entry.institution?.trim() ||
          entry.studyField?.trim() ||
          entry.startYear?.trim() ||
          entry.endYear?.trim()
      );

      if (filteredEducation.length === 0) {
        throw new Error('No valid education entries to save');
      }

      try {
        await updateUserMutation({
          education: filteredEducation.map((entry) => ({
            ...entry,
            startDate: entry.startYear,
            endDate: entry.isPresent ? '' : entry.endYear,
            startYear: undefined,
            endYear: undefined,
          })),
        } as any);

        form.reset({ education: filteredEducation }, { keepValues: true, keepDirty: false });

        queryClient.invalidateQueries();

        segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
          section: 'education',
          previous: JSON.stringify(userData),
          current: JSON.stringify(form.getValues()),
        });

        toaster.showSuccess({ title: 'Education updated' });

        goBack();
      } catch (apiError) {
        throw new Error(
          `API error: ${apiError instanceof Error ? apiError.message : 'Unknown API error'}`
        );
      }
    } catch (error) {
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_FAILED, {
        section: 'education',
        previous: JSON.stringify(userData),
        current: JSON.stringify(form.getValues()),
      });

      toaster.showError({
        title: 'Failed to save education',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  useEffect(() => {
    if (userData) {
      const educationEntries =
        userData?.education?.map((edu: any) => ({
          degree: edu.degree ?? '',
          institution: edu.institution ?? '',
          studyField: edu.studyField ?? '',
          startYear: edu.startDate === 'null' || !edu.startDate ? '' : edu.startDate,
          endYear: edu.endDate === 'null' || !edu.endDate ? '' : edu.endDate,
          isPresent: edu.isPresent ?? false,
        })) || [];

      form.reset({
        education: sortByYear(educationEntries),
      });
    }
  }, [userData]);

  const handleBack = () => {
    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({
    isDirty: form.formState.isDirty,
    onDiscard: handleBack,
  });

  useEffect(() => {
    segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_STARTED, {
      section: 'education',
    });
  }, []);

  const handleDeleteRequest = (index: number) => {
    setPendingDeleteIndex(index);

    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIndex == null) {
      return;
    }

    setDeleting(true);

    try {
      const updatedEducation = [...(userData?.education || [])];

      updatedEducation.splice(pendingDeleteIndex, 1);

      await updateUserMutation({ education: updatedEducation } as any);

      remove(pendingDeleteIndex);

      queryClient.invalidateQueries();

      setShowDeleteModal(false);

      setPendingDeleteIndex(null);

      toaster.showSuccess({ title: 'Education entry deleted' });
    } catch (error) {
      toaster.showError({
        title: 'Failed to delete entry',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setDeleting(false);
    }
  };

  const addEmptyEntry = () => {
    const emptyEntry: Education = {
      degree: '',
      institution: '',
      studyField: '',
      startYear: '',
      endYear: '',
      isPresent: false,
    };

    append(emptyEntry, { shouldFocus: false });

    const newIndex = fields.length;

    setExpandedItem([`item-${newIndex}`]);
  };

  if (isLoading) {
    return <Spinner size="large" className="flex-1" />;
  }

  return (
    <VStack
      className={twJoin(
        isWeb ? 'mx-auto w-full max-w-screen-md flex-1 px-5 md:px-10' : 'flex-1 bg-background-0'
      )}>
      <Header
        contentScrollsUnderHeader
        {...getCancelHeaderProps(confirmCancel)}
        title="Edit Education"
        rightItems={[
          headerItem.save({
            onPress: form.handleSubmit(onSubmit),
            isDisabled: !form.formState.isDirty,
            isSaving: form.formState.isSubmitting,
          }),
        ]}
      />

      {discardModal}
      {!isWeb ? (
        <KeyboardAwareScrollView
          contentInsetAdjustmentBehavior="automatic"
          mode="layout"
          ref={keyboardAwareScrollRef}
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          enabled
          extraKeyboardSpace={120}>
          <VStack space="sm" className="px-4 pt-6">
            {educationErrorCount > 0 && (
              <Alert action="error" className="mx-4 my-2">
                <AlertText size="md">
                  {`Complete required fields in ${educationErrorCount} education entr${educationErrorCount === 1 ? 'y' : 'ies'} before saving.`}
                </AlertText>
              </Alert>
            )}
            {fields.map((field, index) => (
              <EducationEntry
                key={field.id}
                field={field}
                index={index}
                form={form}
                hasSubmitted={hasSubmitted}
                onDeleteRequest={handleDeleteRequest}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
              />
            ))}
            <Button variant="outline" size="sm" onPress={addEmptyEntry} className="mt-4 self-start">
              <Icon as={Plus} size="sm" className="mr-2 text-typography-500" />
              <ButtonText>Add Education</ButtonText>
            </Button>
          </VStack>
        </KeyboardAwareScrollView>
      ) : (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag">
          <VStack space="sm" className="px-4 pt-6">
            {educationErrorCount > 0 && (
              <Alert action="error" className="mx-4 my-2">
                <AlertText size="md">
                  {`Complete required fields in ${educationErrorCount} education entr${educationErrorCount === 1 ? 'y' : 'ies'} before saving.`}
                </AlertText>
              </Alert>
            )}
            {fields.map((field, index) => (
              <EducationEntry
                key={field.id}
                field={field}
                index={index}
                form={form}
                hasSubmitted={hasSubmitted}
                onDeleteRequest={handleDeleteRequest}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
              />
            ))}
            <Button variant="outline" size="sm" onPress={addEmptyEntry} className="mt-4 self-start">
              <Icon as={Plus} size="sm" className="mr-2 text-typography-500" />
              <ButtonText>Add Education</ButtonText>
            </Button>
          </VStack>
        </ScrollView>
      )}

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="h2-xl">Delete entry</Heading>
            <ModalCloseButton onPress={() => setShowDeleteModal(false)}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-4">
            <Button
              disabled={deleting}
              className={twJoin('w-full', deleting && 'opacity-50')}
              onPress={handleConfirmDelete}>
              {deleting ? <Spinner size="small" /> : <ButtonText>Delete entry</ButtonText>}
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => setShowDeleteModal(false)}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default EditEducationScreen;
